import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, X, Plus, Send, Sun, Moon, Monitor, Palette } from "lucide-react";

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newMonthlyRec, setNewMonthlyRec] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/notifications/config"); setCfg(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/notifications/config", patch);
      setCfg(r.data);
      toast.success("Configuração salva");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const addRecipient = (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !cfg) return;
    const next = [...(cfg.recipients || []), newEmail.trim()];
    setNewEmail("");
    save({ recipients: Array.from(new Set(next)) });
  };
  const removeRecipient = (email) => {
    if (!cfg) return;
    save({ recipients: (cfg.recipients || []).filter((e) => e !== email) });
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Informe um e-mail para o teste"); return; }
    try {
      await api.post("/notifications/test", { recipient: testEmail });
      toast.success(`Teste enviado para ${testEmail}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!cfg) return null;

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Sistema</div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight">Configurações</h1>
        <p className="mt-2 text-sm text-muted-foreground">Personalize a aparência do sistema e as notificações por e-mail.</p>
      </div>

      <Card data-testid="theme-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <Palette className="h-5 w-5 text-primary" /> Aparência e Tema
          </CardTitle>
          <CardDescription>
            Escolha entre o tema escuro (Fundo Preto) ou claro (Fundo Branco) para a interface do sistema.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => setTheme("dark")}
              data-testid="theme-option-dark"
              className={`flex flex-col items-center justify-between rounded-lg border-2 p-4 text-left transition-all ${
                theme === "dark"
                  ? "border-primary bg-primary/10 text-foreground shadow-sm"
                  : "border-border bg-card hover:bg-accent text-muted-foreground hover:text-foreground"
              }`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900 text-zinc-100">
                <Moon className="h-5 w-5" />
              </div>
              <div className="mt-3 text-center">
                <div className="text-sm font-semibold">Fundo Preto (Escuro)</div>
                <div className="text-xs text-muted-foreground">Ideal para ambientes escuros</div>
              </div>
              {theme === "dark" && (
                <Badge className="mt-3 bg-primary text-primary-foreground text-[10px]">Ativo</Badge>
              )}
            </button>

            <button
              type="button"
              onClick={() => setTheme("light")}
              data-testid="theme-option-light"
              className={`flex flex-col items-center justify-between rounded-lg border-2 p-4 text-left transition-all ${
                theme === "light"
                  ? "border-primary bg-primary/10 text-foreground shadow-sm"
                  : "border-border bg-card hover:bg-accent text-muted-foreground hover:text-foreground"
              }`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <Sun className="h-5 w-5" />
              </div>
              <div className="mt-3 text-center">
                <div className="text-sm font-semibold">Fundo Branco (Claro)</div>
                <div className="text-xs text-muted-foreground">Interface limpa de alto contraste</div>
              </div>
              {theme === "light" && (
                <Badge className="mt-3 bg-primary text-primary-foreground text-[10px]">Ativo</Badge>
              )}
            </button>

            <button
              type="button"
              onClick={() => setTheme("system")}
              data-testid="theme-option-system"
              className={`flex flex-col items-center justify-between rounded-lg border-2 p-4 text-left transition-all ${
                theme === "system"
                  ? "border-primary bg-primary/10 text-foreground shadow-sm"
                  : "border-border bg-card hover:bg-accent text-muted-foreground hover:text-foreground"
              }`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                <Monitor className="h-5 w-5" />
              </div>
              <div className="mt-3 text-center">
                <div className="text-sm font-semibold">Tema do Sistema</div>
                <div className="text-xs text-muted-foreground">Sincroniza com o SO</div>
              </div>
              {theme === "system" && (
                <Badge className="mt-3 bg-primary text-primary-foreground text-[10px]">Ativo</Badge>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-heading text-lg"><Mail className="h-4 w-4" /> Notificações por e-mail</CardTitle>
              <CardDescription>Ative para enviar um resumo automático para os destinatários configurados.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="enabled" className="text-sm">{cfg.enabled ? "Ativo" : "Inativo"}</Label>
              <Switch id="enabled" checked={!!cfg.enabled}
                onCheckedChange={(v) => save({ enabled: v })}
                disabled={saving} data-testid="notif-enabled-switch" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-2 block">Destinatários</Label>
            <div className="mb-3 flex flex-wrap gap-2">
              {(cfg.recipients || []).length === 0 && (
                <span className="text-xs text-muted-foreground italic">Nenhum destinatário. Adicione ao menos um e-mail.</span>
              )}
              {(cfg.recipients || []).map((r) => (
                <Badge key={r} variant="secondary" className="gap-1 py-1 pl-3 pr-1" data-testid={`recipient-${r}`}>
                  {r}
                  <button
                    type="button" onClick={() => removeRecipient(r)}
                    className="rounded p-0.5 hover:bg-muted-foreground/20"
                    data-testid={`remove-recipient-${r}`}
                  ><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
            <form onSubmit={addRecipient} className="flex gap-2">
              <Input type="email" placeholder="email@empresa.com" value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)} data-testid="new-recipient-input" />
              <Button type="submit" disabled={saving} data-testid="add-recipient-btn"><Plus className="h-4 w-4" /></Button>
            </form>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <Label>Eventos que disparam notificação</Label>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {[
                { key: "notify_on_open", label: "Abertura de chamado", testid: "notif-open" },
                { key: "notify_on_update", label: "Movimentação (status/atribuição)", testid: "notif-update" },
                { key: "notify_on_close", label: "Fechamento / resolução", testid: "notif-close" },
                { key: "notify_on_comment", label: "Novo comentário", testid: "notif-comment" },
                { key: "notify_on_asset_change", label: "Alteração de ativo (responsável/status)", testid: "notif-asset" },
              ].map((it) => (
                <div key={it.key} className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                  <span className="text-sm">{it.label}</span>
                  <Switch
                    checked={!!cfg[it.key]}
                    onCheckedChange={(v) => save({ [it.key]: v })}
                    disabled={saving}
                    data-testid={`${it.testid}-switch`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <Label>Testar envio</Label>
            <div className="flex gap-2">
              <Input type="email" placeholder="destinatario@teste.com" value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email-input" />
              <Button variant="outline" onClick={sendTest} data-testid="send-test-btn"><Send className="mr-2 h-4 w-4" /> Enviar teste</Button>
            </div>
            <p className="text-xs text-muted-foreground">O envio usa a integração Resend gerenciada pelo Emergent — não é necessário configurar SMTP.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-heading text-lg"><Mail className="h-4 w-4" /> Relatório mensal automático</CardTitle>
              <CardDescription>Envia automaticamente o consolidado do mês anterior no dia configurado.</CardDescription>
            </div>
            <Switch checked={!!cfg.monthly_report_enabled}
              onCheckedChange={(v) => save({ monthly_report_enabled: v })}
              disabled={saving} data-testid="monthly-enabled-switch" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Dia do envio</Label>
              <Input type="number" min="1" max="28" value={cfg.monthly_report_day || 1}
                onChange={(e) => save({ monthly_report_day: Math.max(1, Math.min(28, Number(e.target.value))) })}
                disabled={saving} data-testid="monthly-day-input" />
              <p className="mt-1 text-xs text-muted-foreground">Entre 1 e 28. O relatório é referente ao mês anterior.</p>
            </div>
            <div>
              <Label>Último envio automático</Label>
              <div className="mt-2 font-mono text-sm">
                {cfg.monthly_report_last_sent
                  ? new Date(cfg.monthly_report_last_sent).toLocaleString("pt-BR")
                  : <span className="text-muted-foreground italic">Nunca enviado</span>}
              </div>
            </div>
          </div>

          <div>
            <Label>Destinatários do relatório mensal (opcional)</Label>
            <p className="mt-1 text-xs text-muted-foreground">Se vazio, usa a lista de destinatários geral acima.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(cfg.monthly_report_recipients || []).length === 0 && (
                <span className="text-xs text-muted-foreground italic">Sem lista dedicada — usará a lista geral.</span>
              )}
              {(cfg.monthly_report_recipients || []).map((r) => (
                <Badge key={r} variant="secondary" className="gap-1 py-1 pl-3 pr-1" data-testid={`mrec-${r}`}>
                  {r}
                  <button type="button"
                    onClick={() => save({ monthly_report_recipients: (cfg.monthly_report_recipients || []).filter(e => e !== r) })}
                    className="rounded p-0.5 hover:bg-muted-foreground/20">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!newMonthlyRec.trim()) return;
                const next = [...(cfg.monthly_report_recipients || []), newMonthlyRec.trim()];
                setNewMonthlyRec("");
                save({ monthly_report_recipients: Array.from(new Set(next)) });
              }}
              className="mt-2 flex gap-2"
            >
              <Input type="email" placeholder="diretor@empresa.com" value={newMonthlyRec}
                onChange={(e) => setNewMonthlyRec(e.target.value)} data-testid="new-monthly-rec-input" />
              <Button type="submit" disabled={saving} data-testid="add-monthly-rec-btn"><Plus className="h-4 w-4" /></Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
